import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { changePassword, describeAuthError } from "../../app/authFlow.js";
import { ApiClientError } from "../../api/http.js";
import { useSession } from "../../app/session.js";
import { useMe, useMinPasswordLength } from "../../data/account.js";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";
import { masterPasswordHint, masterPasswordProblem } from "../../lib/masterPassword.js";
import { Dialog } from "../../ui/Dialog.js";
import { Checkbox, Field, SecretInput, StrengthMeter } from "../../ui/forms.js";
import { Alert, Button, Section } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";

/**
 * Master password change. The account keys are sealed under the new password in this browser;
 * entries stay as they are, and the server never sees either password.
 */
export function MasterPasswordSection(): ReactNode {
  const me = useMe();
  const { passwordLength } = useSession();
  const minLength = useMinPasswordLength();
  const [open, setOpen] = useState(false);
  const changedAt = me.data?.passwordChangedAt ?? null;
  const tooShort = passwordLength !== undefined && passwordLength < minLength;

  return (
    <Section
      id="master-password"
      title="Master-Passwort"
      description="Es entsperrt deinen Tresor. Beim Ändern bleiben die Einträge unverändert; nur ihr Schlüssel wird neu verschlossen."
    >
      <div className="flex flex-col gap-4">
        {tooShort ? (
          <Alert tone="danger">
            Dein Administrator verlangt mindestens {minLength} Zeichen. Dein Master-Passwort ist kürzer – ändere es, bevor du weiterarbeitest.
          </Alert>
        ) : null}
        <div className="flex flex-col gap-3 rounded-lg border border-line bg-ink-2 p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm leading-relaxed text-mist">
            {changedAt === null ? "Noch nie geändert." : (
              <span title={formatDateTime(changedAt)}>Zuletzt geändert {formatRelativeTime(changedAt, Date.now())}.</span>
            )}
          </p>
          <Button variant="secondary" icon={KeyRound} onClick={(): void => setOpen(true)}>
            Ändern
          </Button>
        </div>
      </div>
      {open ? <ChangeDialog minLength={minLength} onClose={(): void => setOpen(false)} /> : null}
    </Section>
  );
}

function ChangeDialog(props: { readonly minLength: number; readonly onClose: () => void }): ReactNode {
  const { state, notePasswordLength } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy || state.status !== "unlocked") {
      return;
    }
    const problem = current === ""
      ? "Bitte gib dein aktuelles Master-Passwort ein."
      : masterPasswordProblem(next, confirm, current, props.minLength) ?? (consent ? undefined : "Bitte bestätige den Hinweis.");
    setError(problem);
    if (problem !== undefined) {
      return;
    }
    setBusy(true);
    try {
      await changePassword(state.client, state.email, current, next);
      notePasswordLength(next.length);
      await queryClient.invalidateQueries();
      toast.notify("Master-Passwort geändert. Alle anderen Sitzungen wurden beendet.", { tone: "success" });
      props.onClose();
    } catch (err: unknown) {
      setError(err instanceof ApiClientError && err.status === 403 ? err.message : describeAuthError(err));
    } finally {
      setCurrent("");
      setNext("");
      setConfirm("");
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={(): void => {
        if (!busy) {
          props.onClose();
        }
      }}
      dismissible={!busy}
      title="Master-Passwort ändern"
      description="Andere Geräte, die Desktop-App und die Browser-Erweiterung werden danach abgemeldet."
      variant="center"
      size="sm"
    >
      <form onSubmit={(event): void => void submit(event)} className="flex flex-col gap-5 p-5" noValidate>
        <Field id="pw-current" label="Aktuelles Master-Passwort">
          <SecretInput id="pw-current" autoComplete="current-password" autoFocus value={current} disabled={busy} onChange={(event): void => setCurrent(event.target.value)} revealLabel="aktuelles Master-Passwort" />
        </Field>
        <Field id="pw-next" label="Neues Master-Passwort" hint={masterPasswordHint(props.minLength)}>
          <SecretInput id="pw-next" autoComplete="new-password" value={next} disabled={busy} onChange={(event): void => setNext(event.target.value)} revealLabel="neues Master-Passwort" />
        </Field>
        <StrengthMeter password={next} />
        <Field id="pw-confirm" label="Neues Master-Passwort wiederholen">
          <SecretInput id="pw-confirm" autoComplete="new-password" value={confirm} disabled={busy} onChange={(event): void => setConfirm(event.target.value)} revealLabel="Wiederholung" />
        </Field>
        <Checkbox id="pw-consent" checked={consent} onChange={setConsent}>
          Ich habe mir das neue Master-Passwort sicher notiert. Ohne es öffnet nur mein Notfall-Kit den Tresor – falls ich eines habe.
        </Checkbox>
        {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
        {busy ? (
          <p className="text-sm text-mist" role="status">Schlüssel werden abgeleitet …</p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={props.onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" loading={busy}>
            Passwort ändern
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
